import { Money } from '../ui/Money';
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TrendingUp, Plus, ChevronDown, ChevronUp, History, Trash2, FileText, FileDown, Clock } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { PromotionStatusActions, STATUS_META } from './PromotionStatusActions';
import { SalarySplitInput } from './SalarySplit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromotionHistorySection = ({ 
  promotionHistory = [], 
  currentPosition,
  currentSalary,
  employeeId,
  onAddPromotion,
  onDeletePromotion,
  onPromotionChanged,
  isViewer = false
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isHistoricalMode, setIsHistoricalMode] = useState(false);
  const [signatories, setSignatories] = useState([]);
  // Per-promotion signatory selection. Keyed by promotion.id, defaults to 0.
  const [signatoryByPromo, setSignatoryByPromo] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/api/employees/promotion/signatories`)
      .then(res => setSignatories(res.data || []))
      .catch(err => {
        console.error('Failed to load signatories', err);
        toast({
          title: 'Could not load signatories',
          description: err?.response?.data?.detail || 'Falling back to the default signer.',
          variant: 'destructive',
        });
      });
  }, []);
  const [newPromotion, setNewPromotion] = useState({
    previous_position: '',
    previous_salary: {
      basic: 0,
      housing: 0,
      transportation: 0,
      other: 0
    },
    new_position: '',
    new_salary: {
      basic: currentSalary?.basic || 0,
      housing: currentSalary?.housing || 0,
      transportation: currentSalary?.transportation || 0,
      other: currentSalary?.other || 0
    },
    effective_date: '',
    reason: '',
    approved_by: ''
  });

  const handleSalaryChange = (type, field, value) => {
    const salaryKey = type === 'previous' ? 'previous_salary' : 'new_salary';
    setNewPromotion(prev => ({
      ...prev,
      [salaryKey]: {
        ...prev[salaryKey],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleSubmit = () => {
    if (!newPromotion.new_position || !newPromotion.effective_date) {
      alert('Please fill in required fields (New Position and Effective Date)');
      return;
    }
    if (isHistoricalMode && !newPromotion.previous_position) {
      alert('Please fill in the Previous Position for historical records');
      return;
    }
    
    const promotionData = {
      ...newPromotion,
      is_historical: isHistoricalMode
    };
    
    onAddPromotion(promotionData);
    resetForm();
  };

  const resetForm = () => {
    setNewPromotion({
      previous_position: '',
      previous_salary: {
        basic: 0,
        housing: 0,
        transportation: 0,
        other: 0
      },
      new_position: '',
      new_salary: {
        basic: currentSalary?.basic || 0,
        housing: currentSalary?.housing || 0,
        transportation: currentSalary?.transportation || 0,
        other: currentSalary?.other || 0
      },
      effective_date: '',
      reason: '',
      approved_by: ''
    });
    setShowAddForm(false);
    setIsHistoricalMode(false);
  };

  const openAddForm = (historical) => {
    setIsHistoricalMode(historical);
    if (!historical) {
      // For regular promotion, pre-fill new salary with current
      setNewPromotion(prev => ({
        ...prev,
        new_salary: {
          basic: currentSalary?.basic || 0,
          housing: currentSalary?.housing || 0,
          transportation: currentSalary?.transportation || 0,
          other: currentSalary?.other || 0
        }
      }));
    }
    setShowAddForm(true);
  };

  const totalPrevSalary = newPromotion.previous_salary.basic + 
    newPromotion.previous_salary.housing + 
    newPromotion.previous_salary.transportation + 
    newPromotion.previous_salary.other;

  const totalNewSalary = newPromotion.new_salary.basic + 
    newPromotion.new_salary.housing + 
    newPromotion.new_salary.transportation + 
    newPromotion.new_salary.other;

  const currentTotalSalary = (currentSalary?.basic || 0) + 
    (currentSalary?.housing || 0) + 
    (currentSalary?.transportation || 0) + 
    (currentSalary?.other || 0);

  // Sort promotion history by effective_date
  const sortedHistory = [...promotionHistory].sort((a, b) => {
    const dateA = new Date(a.effective_date);
    const dateB = new Date(b.effective_date);
    return dateB - dateA; // Most recent first
  });

  const handleDownloadLetter = async (promotion, fileFormat, kind = 'revision') => {
    if (!employeeId || !promotion?.id) return;
    try {
      const sigIdx = signatoryByPromo[promotion.id] ?? 0;
      const response = await axios.get(
        `${API_URL}/api/employees/${employeeId}/promotion/${promotion.id}/letter?format=${fileFormat}&signatory_index=${sigIdx}&kind=${kind}`,
        { responseType: 'blob' }
      );
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      const ext = fileFormat === 'docx' ? 'docx' : 'pdf';
      const fallback = `Salary_Revision_${(promotion.new_position || 'letter').replace(/\s+/g, '_')}.${ext}`;
      const filename = match ? match[1].replace(/"/g, '').trim() : fallback;
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Letter download failed:', err);
      toast({
        title: 'Letter download failed',
        description: err?.response?.data?.detail || err?.message || 'Please try again in a moment.',
        variant: 'destructive',
      });
    }
  };

  const todayISO = new Date().toISOString().split('T')[0];

  return (
    <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-emerald-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          Promotion History
        </h3>
        <div className="flex gap-2">
          {promotionHistory.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
              {promotionHistory.length} Promotion{promotionHistory.length > 1 ? 's' : ''}
            </Button>
          )}
          {!isViewer && employeeId && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => openAddForm(true)}
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="add-historical-promotion-btn"
              >
                <History className="h-4 w-4 mr-1" />
                Add Old Record
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => openAddForm(false)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-promotion-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Promotion
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Current Position Display */}
      <div className="bg-white p-3 rounded-lg border mb-4">
        <p className="text-sm text-gray-500">Current Position</p>
        <p className="font-semibold text-lg">{currentPosition || 'Not Set'}</p>
        <p className="text-sm text-gray-500 mt-1">Current Total Salary: <span className="text-green-600 font-medium"><Money value={currentTotalSalary} /></span></p>
      </div>

      {/* Promotion History List */}
      {showHistory && promotionHistory.length > 0 && (
        <div className="space-y-3 mb-4">
          <p className="text-sm font-medium text-gray-600">Previous Promotions:</p>
          {sortedHistory.map((promo, index) => {
            const prevTotal = (promo.previous_salary?.basic || 0) + 
              (promo.previous_salary?.housing || 0) + 
              (promo.previous_salary?.transportation || 0) + 
              (promo.previous_salary?.other || 0);
            const newTotal = (promo.new_salary?.basic || 0) + 
              (promo.new_salary?.housing || 0) + 
              (promo.new_salary?.transportation || 0) + 
              (promo.new_salary?.other || 0);
            const salaryIncrease = newTotal - prevTotal;
            
            return (
              <div key={index} className={`bg-white p-3 rounded-lg border border-green-200 ${promo.status === 'reverted' ? 'opacity-70' : ''}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {promo.previous_position} → <span className={promo.status === 'reverted' ? 'line-through text-gray-500' : 'text-green-600'}>{promo.new_position}</span>
                      {promo.pending_apply && (promo.effective_date || '') > todayISO && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase font-semibold">
                          <Clock className="h-3 w-3" /> Scheduled
                        </span>
                      )}
                      {STATUS_META[promo.status] && (
                        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${STATUS_META[promo.status].cls}`} data-testid={`promotion-status-${index}`}>
                          {STATUS_META[promo.status].label}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      Effective: {promo.effective_date ? format(new Date(promo.effective_date), 'dd MMM yyyy') : 'N/A'}
                    </p>
                    {promo.reason && (
                      <p className="text-xs text-gray-500 mt-1">Reason: {promo.reason}</p>
                    )}
                    {promo.approved_by && (
                      <p className="text-xs text-gray-500 mt-1">Approved by: <span className="font-medium text-gray-700">{promo.approved_by}</span></p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <div className="flex items-center gap-1.5 mr-1">
                        <Label className="text-[10px] text-gray-500 whitespace-nowrap">Signed by:</Label>
                        <Select
                          value={String(signatoryByPromo[promo.id] ?? 0)}
                          onValueChange={(v) => setSignatoryByPromo(prev => ({ ...prev, [promo.id]: parseInt(v, 10) }))}
                        >
                          <SelectTrigger
                            className="h-7 text-[11px] w-[200px]"
                            data-testid={`promotion-signatory-${index}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {signatories.length === 0 ? (
                              <SelectItem value="0">Joy P Samuel — Group Finance Director</SelectItem>
                            ) : signatories.map((s, i) => (
                              <SelectItem key={i} value={String(i)}>{s.name} — {s.designation}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px] border-blue-300 text-blue-700 hover:bg-blue-50"
                        onClick={() => handleDownloadLetter(promo, 'pdf')}
                        data-testid={`promotion-download-pdf-${index}`}
                        title="Download Salary Revision Letter (PDF)"
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => handleDownloadLetter(promo, 'docx')}
                        data-testid={`promotion-download-word-${index}`}
                        title="Download Salary Revision Letter (Word)"
                      >
                        <FileDown className="h-3.5 w-3.5 mr-1" /> Word
                      </Button>
                    </div>
                    {employeeId && (
                      <PromotionStatusActions employeeId={employeeId} promo={promo} index={index} isViewer={isViewer}
                        onChanged={onPromotionChanged} onDownload={handleDownloadLetter} />
                    )}
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-right">
                      <p className={`text-sm font-medium ${promo.status === 'reverted' ? 'line-through text-gray-400' : salaryIncrease >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {salaryIncrease >= 0 ? '+' : ''}<Money value={salaryIncrease} />
                      </p>
                      <p className="text-xs text-gray-500">
                        <Money value={prevTotal} /> → <Money value={newTotal} />
                      </p>
                    </div>
                    {!isViewer && onDeletePromotion && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => {
                          if (window.confirm('Delete this promotion record?')) {
                            onDeletePromotion(promo.id);
                          }
                        }}
                        data-testid={`delete-promotion-${index}`}
                        title="Delete promotion record"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Promotion Form */}
      {showAddForm && (
        <div className={`p-4 rounded-lg border space-y-4 ${isHistoricalMode ? 'bg-amber-50 border-amber-300' : 'bg-white border-green-300'}`}>
          <div className="flex items-center justify-between">
            <p className={`font-medium ${isHistoricalMode ? 'text-amber-700' : 'text-green-700'}`}>
              {isHistoricalMode ? '📜 Add Historical Record (Old Promotion)' : 'Add New Promotion'}
            </p>
            {isHistoricalMode && (
              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded">
                Will NOT change current position/salary
              </span>
            )}
          </div>
          
          {isHistoricalMode && (
            <>
              {/* Previous Position and Salary - Only for historical */}
              <div className="border-b pb-4">
                <Label className="text-amber-700 font-medium mb-2 block">Previous (Before Promotion)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <Label>Previous Position *</Label>
                    <Input
                      value={newPromotion.previous_position}
                      onChange={(e) => setNewPromotion(prev => ({ ...prev, previous_position: e.target.value }))}
                      placeholder="e.g., Junior Manager"
                      data-testid="promotion-previous-position"
                    />
                  </div>
                </div>
                <Label className="text-xs mb-2 block">Previous Salary Components (AED)</Label>
                <SalarySplitInput onSplit={s => setNewPromotion(prev => ({ ...prev, previous_salary: s }))} testId="promotion-prev-total-split" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Basic</Label>
                    <Input
                      type="number"
                      value={newPromotion.previous_salary.basic}
                      onChange={(e) => handleSalaryChange('previous', 'basic', e.target.value)}
                      data-testid="promotion-prev-salary-basic"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Housing</Label>
                    <Input
                      type="number"
                      value={newPromotion.previous_salary.housing}
                      onChange={(e) => handleSalaryChange('previous', 'housing', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Transport</Label>
                    <Input
                      type="number"
                      value={newPromotion.previous_salary.transportation}
                      onChange={(e) => handleSalaryChange('previous', 'transportation', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Other</Label>
                    <Input
                      type="number"
                      value={newPromotion.previous_salary.other}
                      onChange={(e) => handleSalaryChange('previous', 'other', e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-sm mt-2 text-gray-600">
                  Previous Total: <Money value={totalPrevSalary} />
                </p>
              </div>
            </>
          )}

          {/* New Position and Salary */}
          <div>
            <Label className={`font-medium mb-2 block ${isHistoricalMode ? 'text-green-700' : ''}`}>
              {isHistoricalMode ? 'After Promotion' : 'New Position Details'}
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>New Position *</Label>
                <Input
                  value={newPromotion.new_position}
                  onChange={(e) => setNewPromotion(prev => ({ ...prev, new_position: e.target.value }))}
                  placeholder="e.g., Senior Manager"
                  data-testid="promotion-new-position"
                />
              </div>
              <div>
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={newPromotion.effective_date}
                  onChange={(e) => setNewPromotion(prev => ({ ...prev, effective_date: e.target.value }))}
                  data-testid="promotion-effective-date"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">New Salary Components (AED)</Label>
            <SalarySplitInput onSplit={s => setNewPromotion(prev => ({ ...prev, new_salary: s }))} testId="promotion-new-total-split" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Basic *</Label>
                <Input
                  type="number"
                  value={newPromotion.new_salary.basic}
                  onChange={(e) => handleSalaryChange('new', 'basic', e.target.value)}
                  data-testid="promotion-salary-basic"
                />
              </div>
              <div>
                <Label className="text-xs">Housing</Label>
                <Input
                  type="number"
                  value={newPromotion.new_salary.housing}
                  onChange={(e) => handleSalaryChange('new', 'housing', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Transport</Label>
                <Input
                  type="number"
                  value={newPromotion.new_salary.transportation}
                  onChange={(e) => handleSalaryChange('new', 'transportation', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Other</Label>
                <Input
                  type="number"
                  value={newPromotion.new_salary.other}
                  onChange={(e) => handleSalaryChange('new', 'other', e.target.value)}
                />
              </div>
            </div>
            <p className="text-sm mt-2 text-green-600 font-medium">
              New Total: <Money value={totalNewSalary} />
              {isHistoricalMode && totalNewSalary > totalPrevSalary && (
                <span className="text-xs ml-2">(+<Money value={totalNewSalary - totalPrevSalary} /> increase)</span>
              )}
              {!isHistoricalMode && totalNewSalary > currentTotalSalary && (
                <span className="text-xs ml-2">(+<Money value={totalNewSalary - currentTotalSalary} /> increase)</span>
              )}
            </p>
          </div>

          <div>
            <Label>Reason for Promotion</Label>
            <Textarea
              value={newPromotion.reason}
              onChange={(e) => setNewPromotion(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g., Excellent performance, completed 2 years..."
              rows={2}
              data-testid="promotion-reason"
            />
          </div>

          <div>
            <Label>Approved By</Label>
            <Select
              value={newPromotion.approved_by || ''}
              onValueChange={(v) => setNewPromotion(prev => ({ ...prev, approved_by: v }))}
            >
              <SelectTrigger data-testid="promotion-approved-by">
                <SelectValue placeholder="Select approver from signatory list" />
              </SelectTrigger>
              <SelectContent>
                {signatories.length === 0 ? (
                  <SelectItem value="Joy P Samuel">Joy P Samuel — Group Finance Director</SelectItem>
                ) : signatories.map((s, i) => (
                  <SelectItem key={i} value={s.name}>{s.name} — {s.designation}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400 mt-1">Recorded on the salary revision and shown in the HR report.</p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={resetForm}>
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleSubmit}
              className={isHistoricalMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
              data-testid="submit-promotion-btn"
            >
              {isHistoricalMode ? 'Save Historical Record' : 'Save Promotion'}
            </Button>
          </div>
        </div>
      )}

      {!employeeId && !isViewer && (
        <p className="text-sm text-gray-500 italic">Save the employee first to add promotions</p>
      )}
    </div>
  );
};

export default PromotionHistorySection;
